import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import React, { memo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { HashLink } from "react-router-hash-link";

const navigation = [
  { name: "Home", href: "/#home" },
  { name: "About Us", href: "/#about" },
  { name: "Solutions", href: "/#solutions" },
  { name: "Our Certifications", href: "/#certifications" },
  // { name: "Careers", href: "/careers" },
];

// Memoized navigation item component
const NavItem = memo(({ item, isActive, onClick, isMobile = false }) => {
  const baseClasses = isMobile 
    ? "block text-base font-medium px-3 py-2 rounded-md transition-colors duration-200 w-full text-left"
    : "text-sm font-medium whitespace-nowrap px-4 py-2 rounded-md transition-colors duration-200";
  
  const activeClasses = isActive 
    ? "text-primary bg-text-secondary/10" 
    : "text-background-secondary hover:bg-text-secondary hover:text-primary";
  
  if (item.href.includes("#")) {
    return (
      <HashLink
        to={item.href}
        smooth
        onClick={onClick}
        className={`${baseClasses} ${activeClasses}`}
      >
        {item.name}
      </HashLink>
    );
  }
  
  return (
    <NavLink
      to={item.href}
      onClick={onClick}
      className={({ isActive }) => `${baseClasses} ${isActive ? "text-primary bg-text-secondary/10" : "text-background-secondary hover:bg-text-secondary hover:text-primary"}`}
    >
      {item.name}
    </NavLink>
  );
});

NavItem.displayName = 'NavItem';

// function classNames(...classes) {
//   return classes.filter(Boolean).join(" ");
// }

export default function NavBar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Helper function to determine if a link is active
  const isActive = (href) => {
    if (href === "/") {
      return location.pathname === "/" && !location.hash;
    }
    if (href.startsWith("/#")) {
      return location.pathname === "/" && location.hash === href.substring(1);
    }
    return location.pathname === href;
  };

  return (
    <header className="bg-background-primary shadow-md">
      {/* Logo Section - Optimized size */}
      <div className="flex justify-center py-2">
        <img
          className="h-[25vh] max-h-[250px] min-h-[50px] w-auto"
          src="./k-pack-logo.webp"
          alt="K-Pack logo"
          loading="eager"
          fetchpriority="high"
        />
      </div>

      {/* Navbar Section */}
      <nav className="bg-background-primary p-2">
        <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
          <div className="relative flex items-center justify-between">
            {/* Mobile menu button */}
            <div className="flex items-center sm:hidden">
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="inline-flex items-center justify-center rounded-md p-2 text-primary hover:bg-text-secondary hover:text-background-secondary focus:outline-none focus:ring-2 focus:ring-inset focus:ring-background-secondary"
                aria-expanded={menuOpen}
              >
                <span className="sr-only">
                  {menuOpen ? 'Close main menu' : 'Open main menu'}
                </span>
                {menuOpen ? (
                  <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>

            {/* Desktop menu */}
            <div className="hidden sm:flex sm:items-center sm:justify-center sm:space-x-6 mx-auto">
              {navigation.map((item) => (
                <NavItem
                  key={item.name}
                  item={item}
                  isActive={isActive(item.href)}
                />
              ))}
              <HashLink
                to="/#contact"
                smooth
                className="bg-accent text-background-primary rounded-md px-4 py-2 text-sm font-medium hover:bg-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background-primary"
                >
                Contact Us
              </HashLink>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div className={`sm:hidden transition-all duration-300 ease-in-out ${menuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
          <div className="px-2 pt-2 pb-3 space-y-1 bg-background-primary border-t border-text-secondary/20 mt-2 mb-4">
            {navigation.map((item) => (
              <NavItem
                key={item.name}
                item={item}
                isActive={isActive(item.href)}
                onClick={() => setMenuOpen(false)}
                isMobile={true}
              />
            ))}
            <HashLink
              to="/#contact"
              smooth
              onClick={() => setMenuOpen(false)}
              className="inline-block bg-accent text-background-primary rounded-md px-4 py-2 text-base font-medium hover:bg-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background-primary mt-3"
            >
              Contact Us
            </HashLink>
          </div>
        </div>
      </nav>
    </header>
  );
}